/**
 * Chart primitives for the Security Overview — SentinelOne-inspired
 * donut, hourly area trend, and horizontal tactic bars. Themed via
 * semantic tokens so they respect light/dark mode.
 */
import * as React from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
} from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import type { AlertSeverity, EdrAlert } from "@/types/edr";

const SEV_COLOR: Record<AlertSeverity, string> = {
  critical: "hsl(var(--destructive))",
  high: "hsl(var(--destructive) / 0.7)",
  medium: "hsl(var(--warning))",
  low: "hsl(var(--info))",
  info: "hsl(var(--muted-foreground) / 0.55)",
};
const SEV_LABEL: Record<AlertSeverity, string> = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
  low: "Low",
  info: "Info",
};
const SEV_ORDER: AlertSeverity[] = ["critical", "high", "medium", "low", "info"];

/* ---------------- Severity Donut ---------------- */

export function SeverityDonut({
  alerts,
  loading,
}: {
  alerts: EdrAlert[];
  loading?: boolean;
}) {
  const data = SEV_ORDER.map((s) => ({
    name: SEV_LABEL[s],
    value: alerts.filter((a) => a.severity === s).length,
    color: SEV_COLOR[s],
  })).filter((d) => d.value > 0);
  const total = data.reduce((acc, d) => acc + d.value, 0);

  if (loading) return <Skeleton className="h-[200px] w-full" />;

  return (
    <div className="relative h-[200px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data.length ? data : [{ name: "None", value: 1, color: "hsl(var(--muted))" }]}
            innerRadius={56}
            outerRadius={82}
            paddingAngle={1}
            dataKey="value"
            stroke="hsl(var(--background))"
            strokeWidth={2}
          >
            {(data.length ? data : [{ color: "hsl(var(--muted))" }]).map((d, i) => (
              <Cell key={i} fill={d.color} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              background: "hsl(var(--popover))",
              border: "1px solid hsl(var(--border))",
              fontSize: 11,
              borderRadius: 4,
            }}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-3xl font-light leading-none tabular-nums text-foreground">
          {total}
        </div>
        <div className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
          Open alerts
        </div>
      </div>
    </div>
  );
}

export function SeverityLegend({ alerts }: { alerts: EdrAlert[] }) {
  return (
    <ul className="grid grid-cols-2 gap-x-3 gap-y-1.5 px-2 pb-2 text-[11px]">
      {SEV_ORDER.map((s) => {
        const v = alerts.filter((a) => a.severity === s).length;
        return (
          <li key={s} className="flex items-center gap-1.5">
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ background: SEV_COLOR[s] }}
            />
            <span className="text-muted-foreground">{SEV_LABEL[s]}</span>
            <span className="ml-auto tabular-nums text-foreground">{v}</span>
          </li>
        );
      })}
    </ul>
  );
}

/* ---------------- Alerts Trend (24h) ---------------- */

export function AlertsTrend({
  alerts,
  loading,
}: {
  alerts: EdrAlert[];
  loading?: boolean;
}) {
  const buckets = React.useMemo(() => {
    const now = Date.now();
    const arr = Array.from({ length: 24 }, (_, i) => {
      const t = now - (23 - i) * 3600_000;
      const d = new Date(t);
      return {
        t,
        label: `${d.getHours().toString().padStart(2, "0")}:00`,
        critical: 0,
        high: 0,
        other: 0,
      };
    });
    for (const a of alerts) {
      const ts = new Date(a.ts).getTime();
      const hoursAgo = Math.floor((now - ts) / 3600_000);
      if (hoursAgo < 0 || hoursAgo > 23) continue;
      const b = arr[23 - hoursAgo];
      if (a.severity === "critical") b.critical += 1;
      else if (a.severity === "high") b.high += 1;
      else b.other += 1;
    }
    return arr;
  }, [alerts]);

  if (loading) return <Skeleton className="h-[200px] w-full" />;

  return (
    <div className="h-[200px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={buckets} margin={{ top: 6, right: 8, left: -16, bottom: 0 }}>
          <defs>
            <linearGradient id="gCrit" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--destructive))" stopOpacity={0.45} />
              <stop offset="100%" stopColor="hsl(var(--destructive))" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gHigh" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--warning))" stopOpacity={0.4} />
              <stop offset="100%" stopColor="hsl(var(--warning))" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gOther" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--info))" stopOpacity={0.35} />
              <stop offset="100%" stopColor="hsl(var(--info))" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
            interval={3}
            tickLine={false}
            axisLine={{ stroke: "hsl(var(--border))" }}
          />
          <YAxis
            tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
            tickLine={false}
            axisLine={false}
            width={28}
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{
              background: "hsl(var(--popover))",
              border: "1px solid hsl(var(--border))",
              fontSize: 11,
              borderRadius: 4,
            }}
          />
          <Area
            type="monotone"
            dataKey="other"
            stackId="1"
            stroke="hsl(var(--info))"
            strokeWidth={1.5}
            fill="url(#gOther)"
          />
          <Area
            type="monotone"
            dataKey="high"
            stackId="1"
            stroke="hsl(var(--warning))"
            strokeWidth={1.5}
            fill="url(#gHigh)"
          />
          <Area
            type="monotone"
            dataKey="critical"
            stackId="1"
            stroke="hsl(var(--destructive))"
            strokeWidth={1.5}
            fill="url(#gCrit)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ---------------- MITRE Tactic Bars ---------------- */

export function TacticBars({
  data,
  loading,
}: {
  data: { tactic: string; count: number }[];
  loading?: boolean;
}) {
  if (loading) return <Skeleton className="h-[200px] w-full" />;
  if (!data.length) {
    return (
      <p className="py-8 text-center text-xs text-muted-foreground">
        No tactic activity yet.
      </p>
    );
  }
  const rows = data.slice(0, 6);
  return (
    <div className="h-[200px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={rows}
          layout="vertical"
          margin={{ top: 4, right: 12, left: 4, bottom: 0 }}
          barCategoryGap={6}
        >
          <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" horizontal={false} />
          <XAxis
            type="number"
            tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
          />
          <YAxis
            dataKey="tactic"
            type="category"
            tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
            axisLine={false}
            tickLine={false}
            width={110}
          />
          <Tooltip
            cursor={{ fill: "hsl(var(--muted) / 0.4)" }}
            contentStyle={{
              background: "hsl(var(--popover))",
              border: "1px solid hsl(var(--border))",
              fontSize: 11,
              borderRadius: 4,
            }}
          />
          <Bar
            dataKey="count"
            fill="hsl(var(--primary))"
            radius={[0, 2, 2, 0]}
            maxBarSize={14}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ---------------- Hero KPI (large number) ---------------- */

export function HeroKpi({
  label,
  value,
  sublabel,
  trend,
  tone = "default",
  loading,
}: {
  label: string;
  value: React.ReactNode;
  sublabel?: string;
  trend?: { value: string; up?: boolean };
  tone?: "default" | "danger" | "warning" | "success" | "info";
  loading?: boolean;
}) {
  const toneClass = {
    default: "text-foreground",
    danger: "text-destructive",
    warning: "text-warning",
    success: "text-success",
    info: "text-info",
  }[tone];
  return (
    <div className="flex flex-col gap-1 px-4 py-4">
      <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </span>
      {loading ? (
        <Skeleton className="h-11 w-24" />
      ) : (
        <div className="flex items-baseline gap-2">
          <span className={`text-5xl font-extralight leading-none tabular-nums ${toneClass}`}>
            {value}
          </span>
          {trend ? (
            <span
              className={`text-[11px] font-medium tabular-nums ${
                trend.up ? "text-destructive" : "text-success"
              }`}
            >
              {trend.up ? "▲" : "▼"} {trend.value}
            </span>
          ) : null}
        </div>
      )}
      {sublabel ? (
        <span className="text-[11px] text-muted-foreground">{sublabel}</span>
      ) : null}
    </div>
  );
}
